import { Menu } from 'antd'
import { Link, useLocation, useMatches } from 'react-router-dom'
import intersection from 'lodash/intersection'
import findKey from 'lodash/findKey'
import union from 'lodash/union'
import React, { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { menu, flat_menu, submenus, parent_children_menu } from './left'
import DebugPopover from './debug_popover'
import { checkAccess } from '../../utils'

const isDebug = process.env.REACT_APP_PERMISSIONS_DEBUG === 'true'

const GenMenu = () => {
  const location = useLocation()
  const userRoles = useSelector(s => s._auth.ca_roles)
  const userPermissions = useSelector(s => s._auth.ca_permissions)
  const matches = useMatches()

  const [def_ok, def_sk] = useMemo(() => {
    let def_ok = [] // opened
    const def_sk = [] // selected by url
    const ls_opened = localStorage.getItem('app.menu.openedKeys')

    if (ls_opened) {
      def_ok = intersection(
        submenus.map(el => el.key),
        JSON.parse(ls_opened),
      )
    }

    const get_url_select = () => {
      let result = undefined

      const filter = flat_menu.filter(el => el.url).filter(el => el.url.includes(location.pathname.split('/')[1]))

      if (filter.length === 0) return result

      if (filter.length === 1) return filter[0]

      let exit = false

      for (const m of matches.reverse()) {
        if (exit) break

        for (const f of filter) {
          if (m.pathname === f.url) {
            result = f
            exit = true
            break
          }
        }
      }

      return result
    }

    const url_select = get_url_select()

    if (url_select) {
      def_sk.push(url_select.key)

      const parent = findKey(parent_children_menu, o => {
        return o.includes(url_select.key)
      })

      const to_join = parent ? [parent] : [url_select.key]

      def_ok = union(def_ok, to_join)
    }

    localStorage.setItem('app.menu.openedKeys', JSON.stringify(def_ok))

    if (def_sk[0]) {
      setTimeout(() => {
        const el = document.querySelector(`[url="${url_select.url}"]`)
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
        }
      }, 1000)
    }

    return [def_ok, def_sk]
  }, [location.pathname, matches])

  const onOpenChange = openedKeys => {
    localStorage.setItem('app.menu.openedKeys', JSON.stringify(openedKeys))
  }

  const isAuthorized = useCallback(
    (roles, permissions) => {
      return checkAccess(roles, userRoles) && checkAccess(permissions, userPermissions)
    },
    [userRoles, userPermissions],
  )

  const getItem = useCallback(
    (itemConfig, isMenuChild) => {
      const {
        // ANTD MenuItemType
        key,
        title,
        label, // also group type
        icon,
        disabled,
        danger,
        // ANTD SubMenuType
        children, // also group type
        popupClassName,
        popupOffset,
        theme,
        onTitleClick,
        // MenuItemGroupType
        type, // must have for 'group' and 'divider'
        // admincore
        url,
        target,
        badge,
        roles,
        permissions,
      } = itemConfig

      // Return divider
      if (type === 'divider') {
        return { type: 'divider' }
      }

      const authorized = isAuthorized(roles, permissions)

      // Return null if not auth and not debug
      if (!authorized && !isDebug) {
        return null
      }

      const toReturn = {
        // ANTD MenuItemType
        key,
        title,
        label, // also group type
        icon,
        disabled,
        danger,
        // ANTD SubMenuType
        children, // also group type
        popupClassName,
        popupOffset,
        theme,
        onTitleClick,
        // MenuItemGroupType
        type, // must have for 'group' and 'divider'
        // admincore
        url,
        target,
        badge,
        roles,
        permissions,
      }

      // Old implementation has only 'title'
      if (!toReturn.label) {
        const label = props => {
          const { content } = props

          const base = () => (
            <span style={{ paddingLeft: isMenuChild ? '16px' : undefined }}>
              {content}

              <span
                style={{
                  position: 'absolute',
                  left: '226px',
                }}
              >
                <DebugPopover authorized={authorized} roles={roles} permissions={permissions} />
              </span>
            </span>
          )

          if (toReturn.url) {
            return <Link to={url}>{base()}</Link>
          }

          return base()
        }

        toReturn.label = label({ content: toReturn.title })
      }

      // Check children as submenu
      if (toReturn.children) {
        const newChildren = toReturn.children.map(e => getItem(e, true))
        toReturn.children = newChildren
      }

      // Remove falsy values
      // Last step to ensure props passing and less bugs in the future
      const keysToDelete = []
      Object.entries(toReturn).forEach(e => {
        const [k, v] = e

        if (v === null || v === undefined) {
          keysToDelete.push(k)
        }
      })

      keysToDelete.forEach(e => {
        delete toReturn[e]
      })

      return toReturn
    },
    [isAuthorized],
  )

  const items = useMemo(() => {
    return menu.map(e => getItem(e))
  }, [getItem])

  return <Menu theme="dark" defaultOpenKeys={def_ok} selectedKeys={def_sk} onOpenChange={onOpenChange} mode="inline" items={items} inlineIndent={8} />
}

export default GenMenu
